import React, { useEffect } from 'react';
import { Button, Typography } from 'antd';
import { WhatsAppOutlined } from '@ant-design/icons';
import { trackClick } from '../utils/clickTracker';
import { motion } from 'framer-motion';
import './PromoTemplate.css';
import { loadGTM } from '../utils/loadGTM';

const { Title, Paragraph, Text } = Typography;

function PromoTemplate({ config }) {
  const {
    id,
    bgColor,
    primaryColor,
    topBarText,
    topBarColor,
    title,
    subtitle,
    discountBadgeText,
    membersText,
    freeText,
    spotsLabel,
    spotsText,
    mainButtonText,
    whatsappLink,
    whatsappLinkApp,
    heroImage,
    phoneImage,
    gtmId,
    membersAvatars,
    textColor,

    // 🔥 FLAGS DE VISIBILIDADE (DEFAULT TRUE)
    showTopBar = true,
    showDiscountBadge = true,
    showSocialProof = true,
    showSocialProoffeminino = true,
    showSpotsSection = true,
    showBenefitsSection = true,
    logofinalfeminino = true,
  } = config;

  const avatarUrls =
    membersAvatars && membersAvatars.length > 0
      ? membersAvatars
      : [
        'https://randomuser.me/api/portraits/men/75.jpg',
        'https://randomuser.me/api/portraits/women/65.jpg',
        'https://randomuser.me/api/portraits/men/32.jpg',
        'https://randomuser.me/api/portraits/women/12.jpg',
      ];

  useEffect(() => {
    if (gtmId) loadGTM(gtmId);
  }, [gtmId]);

  const handleClick = () => {
    if (id) trackClick(id);

    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'lead',
        campaignId: id || null,
        page: window.location.pathname,
        origin: 'whatsapp_cta',
      });
    }

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile && whatsappLinkApp) {
      window.location.href = whatsappLinkApp;
      return;
    }

    if (whatsappLink) {
      if (isMobile) {
        window.location.href = whatsappLink;
      } else {
        window.open(whatsappLink, '_blank', 'noopener');
      }
    }
  };

  const resolvedTextColor = textColor || '#ffffff';

  return (
    <motion.div
      className="promo-page"
      style={{
        backgroundColor: bgColor || '#02142f',
        '--promo-text-color': resolvedTextColor,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >

      {/* ======================== */}
      {/* TOP BAR (condicional) */}
      {/* ======================== */}
      {showTopBar && (
        <motion.div
          className="promo-top-bar"
          style={{
            backgroundColor: topBarColor || "#c92424",
          }}
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Text className="promo-top-bar-text">{topBarText}</Text>
        </motion.div>
      )}

      {/* ======================== */}
      {/* SECTION HERO */}
      {/* ======================== */}
      <section className="promo-hero">
        <div className="promo-hero-content">

          {/* AVATAR */}
          <motion.div
            className="promo-hero-avatar-wrap"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="promo-hero-avatar-circle">
              {heroImage && (
                <img
                  src={heroImage}
                  alt="Apresentador"
                  className="promo-hero-avatar-img"
                />
              )}
            </div>

            {/* ======================== */}
            {/* BADGE DE DESCONTO */}
            {/* ======================== */}
            {showDiscountBadge && discountBadgeText && (
              <motion.div
                className="promo-discount-badge"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <span>Produtos com</span>
                <span>{discountBadgeText}</span>
              </motion.div>
            )}
          </motion.div>

          {/* TÍTULOS */}
          <motion.div
            className="promo-hero-text"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Title level={3} className="promo-hero-title">
              {title}
            </Title>

            {subtitle && (
              <Paragraph className="promo-hero-subtitle">
                {subtitle}
              </Paragraph>
            )}
          </motion.div>

          {/* ======================== */}
            {/* SOCIAL PROOF FEMININO */}
            {/* ======================== */}
            {showSocialProoffeminino && (
              <div className="section-pro-feminino">
                <div>
                  <span>{membersText}</span>
                </div>
                <div>
                  100% Gratuito
                </div>
              </div>
            )}


          {/* CTA */}
          <motion.div
            className="promo-hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Button
              type="primary"
              size="large"
              block
              className="promo-cta-button"
              style={{ backgroundColor: primaryColor || '#27D46A' }}
              onClick={handleClick}
            >
              {mainButtonText}
              <WhatsAppOutlined style={{ marginLeft: 8 }} />
            </Button>

            {/* ======================== */}
            {/* SOCIAL PROOF */}
            {/* ======================== */}
            {showSocialProof && (
              <div className="promo-social-proof">
                <div className="promo-members">
                  <span>{membersText}</span>
                  <div className="promo-avatars-placeholder">
                    {avatarUrls.map((url, index) => (
                      <img
                        key={index}
                        src={url}
                        alt="Membro"
                        className="promo-avatar-img"
                      />
                    ))}
                  </div>
                </div>

                <span className="promo-free-text">{freeText}</span>
              </div>
            )}

            
            {/* ======================== */}
            {/* SPOTS (vagas / barato) */}
            {/* ======================== */}
            {showSpotsSection && (spotsLabel || spotsText) && (
              <div className="promo-spots">
                {/* <span style={{ fontSize: '20px', fontWeight: 'large' }}>
                  PAGUE MAIS BARATO!
                </span> */}

                {spotsLabel && (
                  <span className="promo-spots-label">{spotsLabel}</span>
                )}

                {spotsText && (
                  <span className="promo-spots-chip">{spotsText}</span>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* {logofinalfeminino && (<div className="promo-hero-avatar-circle-final">
        {heroImage && (
          <img
            src={heroImage}
            alt="Apresentador"
            className="promo-hero-avatar-img"
          />
        )}
      </div>)} */}


      {/* ======================== */}
      {/* SEÇÃO BENEFÍCIOS */}
      {/* ======================== */}
      {showBenefitsSection && (
        <section className="promo-benefits">
          <motion.div
            className="promo-benefits-text"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Title level={3} className="promo-benefits-title">
              Receba as melhores promoções todos os dias em seu WhatsApp
            </Title>
          </motion.div>

          <motion.div
            className="promo-benefits-media"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {phoneImage && <img src={phoneImage} alt="Exemplo de promoções" />}
          </motion.div>
        </section>
      )}
    </motion.div>
  );
}

export default PromoTemplate;
